/**
 * PWA Standalone Web App Utilities
 *
 * Handles iOS and Android-specific behavior when app is added to home screen
 * Ensures fresh content when switching back to the app
 */

import { logDebug } from './logger';

/**
 * Check if running in standalone mode (added to home screen)
 */
export const isStandalone = () =>
  window.navigator.standalone === true ||
  window.matchMedia('(display-mode: standalone)').matches;

/**
 * Check if running on iOS device
 */
export const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

/**
 * Check if running on Android device
 */
export const isAndroid = () => /Android/.test(navigator.userAgent);

/**
 * Check if running on mobile device
 */
export const isMobile = () => isIOS() || isAndroid();

// Track last visibility time
let lastVisibilityChange = Date.now();
let visibilityListenerAdded = false;

/**
 * Setup visibility change listener to refresh content when app becomes visible
 * This works for both iOS and Android PWAs
 *
 * CRITICAL: This ONLY activates for actual PWAs (added to home screen)
 * It will NOT activate for:
 * - Safari in normal mode (iOS or desktop)
 * - Chrome in normal mode
 * - Any browser in normal mode
 * - Desktop browsers (even if they support PWA)
 */
export const setupVisibilityRefresh = (refreshThresholdSeconds = 5) => {
  if (visibilityListenerAdded) {
    return; // Already setup
  }

  // CRITICAL: Triple-check we're actually in a PWA, not just a browser
  const isActuallyStandalone = isStandalone();
  const isActuallyMobile = isMobile();

  // MUST be both standalone AND mobile
  // This prevents activation in:
  // - Safari on iPhone/iPad (not standalone)
  // - Chrome on Android (not standalone)
  // - Desktop browsers (not mobile)
  if (!isActuallyStandalone || !isActuallyMobile) {
    // logDebug(
    //   `[PWA Refresh] DISABLED - Not a PWA (standalone: ${isActuallyStandalone}, mobile: ${isActuallyMobile})`
    // );
    return;
  }

  // Extra safety: Log that we're enabling this feature
  // console.warn('[PWA Refresh] ENABLED - App is running as installed PWA');
  // logDebug('[PWA Refresh] Setting up visibility refresh for PWA');

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      const now = Date.now();
      const secondsAway = (now - lastVisibilityChange) / 1000;

      logDebug(`App became visible. Was away for ${secondsAway.toFixed(1)}s`);

      // If app was in background for more than threshold, reload
      if (secondsAway > refreshThresholdSeconds) {
        logDebug('Refreshing app content after being away');
        window.location.reload();
      }
    } else {
      // App going to background
      lastVisibilityChange = Date.now();
      logDebug('App going to background');
    }
  };

  // Add visibility change listener
  document.addEventListener('visibilitychange', handleVisibilityChange);
  visibilityListenerAdded = true;

  // Also handle pageshow event for iOS back-forward cache
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      logDebug('[PWA Refresh] Page restored from bfcache');
      const now = Date.now();
      const secondsAway = (now - lastVisibilityChange) / 1000;
      if (secondsAway > refreshThresholdSeconds) {
        console.warn('[PWA Refresh] Reloading after bfcache restore');
        window.location.reload();
      }
    }
  });

  console.warn('[PWA Refresh] Visibility refresh listener setup complete');
  logDebug('[PWA Refresh] Visibility refresh listener setup complete');
};

/**
 * Force reload the page when PWA is opened (initial load only)
 * This ensures fresh content and updated meta tags
 *
 * CRITICAL: This ONLY runs for iOS PWAs (added to home screen)
 * It will NOT run for Safari in normal mode
 */
export const handleIOSStandaloneReload = () => {
  // MUST be both iOS AND standalone
  if (!isIOS() || !isStandalone()) {
    //logDebug('[PWA Initial Reload] DISABLED - Not an iOS PWA');
    return;
  }

  //logDebug('[PWA Initial Reload] iOS PWA detected');

  // Check if this is a fresh app open (not a navigation within the app)
  const isAppOpen = !document.referrer || document.referrer === '';

  if (isAppOpen) {
    // Use sessionStorage to prevent infinite reload loop
    const hasReloaded = sessionStorage.getItem('pwa_initial_reloaded');

    if (!hasReloaded) {
      sessionStorage.setItem('pwa_initial_reloaded', 'true');
      logDebug('Initial PWA load - forcing reload for fresh content');
      // Force reload to get fresh content
      window.location.reload();
    }
  }
};

/**
 * Clear the reload flag when user navigates away
 * This allows reload on next app open
 */
export const clearIOSStandaloneFlag = () => {
  window.addEventListener('pagehide', () => {
    sessionStorage.removeItem('pwa_initial_reloaded');
  });
};

/**
 * Check if app needs update and prompt user
 */
export const checkForUpdates = async () => {
  if ('serviceWorker' in navigator) {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration) {
      registration.update();
    }
  }
};

/**
 * Get install prompt event for PWA installation
 */
let deferredPrompt = null;

export const setupInstallPrompt = () => {
  // console.log('[PWA Install] Setting up beforeinstallprompt listener');

  // Debug: Check manifest immediately
  const manifestLink = document.querySelector('link[rel="manifest"]');
  // console.log('[PWA Install] Manifest link:', manifestLink?.href);

  // Note: Manifest may show cached values locally - browsers cache manifests aggressively
  // Clear browser cache or use incognito mode to see updated manifest values

  // Debug: Fetch and log manifest content
  if (manifestLink?.href) {
    fetch(manifestLink.href)
      .then((r) => r.json())
      // .then((manifest) => {
      //   console.log('[PWA Install] Manifest content:', manifest);
      //   console.log('[PWA Install] Manifest validation:', {
      //     hasName: !!(manifest.name || manifest.short_name),
      //     hasIcons: manifest.icons?.length >= 2,
      //     has192Icon: manifest.icons?.some((i) => i.sizes === '192x192'),
      //     has512Icon: manifest.icons?.some((i) => i.sizes === '512x512'),
      //     hasStartUrl: !!manifest.start_url,
      //     hasDisplay: !!manifest.display,
      //     displayValue: manifest.display,
      //     preferRelatedApps: manifest.prefer_related_applications,
      //   });
      // })
      .catch((e) =>
        console.error('[PWA Install] Failed to fetch manifest:', e)
      );
  }

  window.addEventListener('beforeinstallprompt', (e) => {
    // console.log(
    //   '[PWA Install] ✅ beforeinstallprompt event fired! PWA is installable'
    // );
    // Prevent the mini-infobar from appearing on mobile
    e.preventDefault();
    // Stash the event so it can be triggered later
    deferredPrompt = e;
    // logDebug('Install prompt available');
  });

  // Log if event never fires
  // setTimeout(() => {
  //   if (!deferredPrompt) {
  //     console.warn(
  //       '[PWA Install] ⚠️ beforeinstallprompt has not fired after 10 seconds.'
  //     );
  //     console.warn('[PWA Install] Possible reasons:');
  //     console.warn(
  //       '  1. User has not spent 30+ seconds on the page (Chrome requirement)'
  //     );
  //     console.warn('  2. Manifest is invalid or missing required fields');
  //     console.warn('  3. Service worker not properly registered');
  //     console.warn('  4. App already installed');
  //     console.warn(
  //       '  → Check Chrome DevTools > Application > Manifest for errors'
  //     );
  //   }
  // }, 10000);
};

export const showInstallPrompt = async () => {
  if (!deferredPrompt) {
    return false;
  }

  // Show the install prompt
  deferredPrompt.prompt();

  // Wait for the user to respond to the prompt
  const { outcome } = await deferredPrompt.userChoice;
  logDebug(`User response to install prompt: ${outcome}`);

  // Clear the deferredPrompt
  deferredPrompt = null;

  return outcome === 'accepted';
};

export const canShowInstallPrompt = () => deferredPrompt !== null;
